import { api } from '../client';
import type { UUID } from '@/types/domain';
import type {
  Announcement,
  AnnouncementInput,
  AnnouncementListParams,
  AppNotification,
  MarkAllReadResult,
  NotificationFeedParams,
  UnreadCount,
} from '@/types/notification';

/*
 * Notifications and announcements.
 *
 * Both feeds are resolved on the server from the signed-in account: nothing
 * here passes a user id, and nothing filters what comes back. Which
 * announcements are editable, and whether a section may narrow an audience,
 * are the server's rules — a refusal arrives as a message to show as it is.
 */

// ------------------------------------------------------------ notifications

export const notificationsApi = {
  /** The caller's own notifications, newest first. */
  feed: (params: NotificationFeedParams = {}) =>
    api.getPage<AppNotification>('/notifications', { params }),

  unreadCount: () => api.get<UnreadCount>('/notifications/unread-count'),

  markRead: (id: UUID) => api.post<AppNotification>(`/notifications/${id}/read`),

  markAllRead: () => api.post<MarkAllReadResult>('/notifications/read-all'),
};

// ------------------------------------------------------------ announcements

export const announcementsApi = {
  /** What is live for the caller's audience right now. A plain list, not paged. */
  feed: () => api.get<Announcement[]>('/announcements/feed'),

  /** The authoring desk: every announcement, drafts included. */
  list: (params: AnnouncementListParams = {}) =>
    api.getPage<Announcement>('/announcements', { params }),

  get: (id: UUID) => api.get<Announcement>(`/announcements/${id}`),
  create: (payload: AnnouncementInput) => api.post<Announcement>('/announcements', payload),

  /** Drafts only; the server refuses an edit to anything published or archived. */
  update: (id: UUID, payload: AnnouncementInput) =>
    api.put<Announcement>(`/announcements/${id}`, payload),

  publish: (id: UUID) => api.post<Announcement>(`/announcements/${id}/publish`),
  archive: (id: UUID) => api.post<Announcement>(`/announcements/${id}/archive`),
};
